import React from 'react';
import { Col, Container, Row, Button, Card, Accordion } from 'react-bootstrap';
import LogoImage from '../LogoImage';
import {
  Diagram3, JournalCheck,
  CheckCircleFill, Key, Eye, ArrowRepeat, Globe, Robot, ShieldLock, CloudArrowUp
} from 'react-bootstrap-icons';

type Props = { onOpenWaitlist?: (reason?: 'request-access' | 'design-partner') => void };

function Home({ onOpenWaitlist }: Props) {
  return (
    <>
      {/* Hero */}
      <section className="hero-section text-light py-5">
        <div className="hero-media" aria-hidden="true">
          <picture className="hero-bg">
            <source type="image/webp" media="(min-width: 1200px)" srcSet={`${process.env.PUBLIC_URL}/img/iStock-1206796363.webp`} />
            <source media="(min-width: 1200px)" srcSet={`${process.env.PUBLIC_URL}/img/iStock-1206796363.jpg`} />
            <source type="image/webp" srcSet={`${process.env.PUBLIC_URL}/img/iStock-1206796363_1024.webp`} />
            <img src={`${process.env.PUBLIC_URL}/img/iStock-1206796363_1024.png`} alt="" className="hero-bg-img" loading="eager" decoding="async" fetchPriority="high" />
          </picture>
          <div className="hero-overlay" />
        </div>
        <Container className="position-relative">
          <Row className="align-items-center gy-4">
            <Col md={6} lg={6} className="hero-content">
              <div className="hero-panel">
                <h1 className="display-5 fw-bold mb-2">The Identity Broker for the Agentic Era.</h1>
                <p className="lead mb-4">AI agents, service accounts, and workload credentials now vastly outnumber people across enterprise environments. Arkodomo issues, governs, and rotates cryptographic identities for every machine — across every cloud — without static secrets.</p>
                <div className="d-flex flex-wrap gap-3">
                  <Button variant="primary" size="lg" onClick={() => onOpenWaitlist && onOpenWaitlist('request-access')}>Join the Waitlist</Button>
                  <Button variant="outline-light" onClick={() => onOpenWaitlist && onOpenWaitlist('design-partner')}>Become a Design Partner</Button>
                </div>
              </div>
            </Col>
            <Col md={6} lg={6} className="text-end hero-logo-col">
              <LogoImage className="hero-logo-img" />
            </Col>
          </Row>
        </Container>
      </section>

      {/* The Problem */}
      <section className="py-5">
        <Container>
          <div className="problem-callout">
            <h2 className="h3 text-light mb-3">The Problem</h2>
            <p className="text-white-50 mb-0">Traditional IAM was designed for people logging in. The machine identity explosion — AI agents, CI/CD runners, microservices, API keys, cloud workload credentials — has created a category of risk that PAM vendors can't address and cloud IAM tools weren't built to govern. Static, long-lived credentials proliferate with no owners, no expiration, and no behavioral visibility. When they're compromised, the blast radius is enterprise-wide.</p>
          </div>
        </Container>
      </section>

      {/* Built for the Agentic Era */}
      <section className="py-5 bg-dark bg-opacity-50">
        <Container>
          <h2 className="h3 text-light mb-2 section-heading">Built for the Agentic Era</h2>
          <p className="text-white-50 mb-4" style={{ fontSize: '0.95rem' }}>Traditional IAM was designed for people. Arkodomo is purpose-built for the non-human world — AI agents, workloads, and services — from day one.</p>
          <Row className="g-4">
            {[{
              icon: <Robot size={22} />,
              title: 'Agent-First Architecture',
              text: 'AI agents are first-class identities in Arkodomo — not an afterthought bolted onto workload identity. Every agent gets a cryptographic identity, capability-scoped credentials, and a full delegation chain so sub-agents can\'t exceed the authority of their parent.'
            }, {
              icon: <CloudArrowUp size={22} />,
              title: 'Cross-Cloud Identity Federation',
              text: 'One policy engine across AWS, Azure, GCP, and on-premises. Arkodomo federates trust across cloud-native identity systems without creating a new silo — so your agents and workloads operate under a consistent policy regardless of where they run.'
            }, {
              icon: <ShieldLock size={22} />,
              title: 'MCP-Native Authentication',
              text: 'The only identity broker with native Model Context Protocol (MCP) support. Agents authenticate via SPIFFE SVIDs; Arkodomo translates those to OAuth 2.1 tokens scoped to specific tools — so AI agents access enterprise systems without static API keys or human credential delegation.'
            }].map((f, i) => (
              <Col key={i} md={4}>
                <Card bg="dark" text="light" className="h-100">
                  <Card.Body className="p-4">
                    <div className="card-icon-badge">{f.icon}</div>
                    <Card.Title className="h5">{f.title}</Card.Title>
                    <Card.Text className="text-white-50 mb-0">{f.text}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* What Arkodomo Does */}
      <section className="py-5 platform-section">
        <Container>
          <h2 className="h3 text-light mb-4 section-heading">The Platform</h2>
          <Row className="g-4">
            {[{
              icon: <Key size={22} />,
              title: 'Provision',
              text: 'A federated identity broker with native connectors to cloud IAM, directory services, secrets managers, orchestration platforms, CI/CD systems, and API key stores. Issue the right credential to the right machine — through a unified policy model that layers on top of your existing infrastructure.'
            }, {
              icon: <Diagram3 size={22} />,
              title: 'Map',
              text: 'Build a live identity relationship graph. Map every machine identity to its owner, its access scope, and its usage pattern. Answer: which service account calls what, and should it?'
            }, {
              icon: <Eye size={22} />,
              title: 'Detect',
              text: 'ML-based behavioral anomaly detection. Flag credential abuse, lateral movement signals, privilege escalation, unexpected region usage, and API call frequency spikes — before they become incidents.'
            }, {
              icon: <JournalCheck size={22} />,
              title: 'Enforce',
              text: 'Automated expiration enforcement, dormant identity cleanup, owner reassignment detection, compliance audit exports, and policy drift alerting. Continuous governance, not quarterly reviews.'
            }].map((f, i) => (
              <Col key={i} md={6}>
                <Card bg="dark" text="light" className="h-100">
                  <Card.Body className="p-4">
                    <div className="card-icon-badge">{f.icon}</div>
                    <Card.Title className="h5">{f.title}</Card.Title>
                    <Card.Text className="text-white-50 mb-0">{f.text}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-5">
        <Container>
          <h2 className="h3 text-light mb-4 section-heading">How It Works</h2>
          <Row className="g-4">
            {[{
              icon: <Globe size={22} />,
              title: 'Connect',
              text: 'Deploy the Arkodomo agent in your VPC or cluster. Connect via native integrations to your cloud provider, orchestration platform, secrets infrastructure, and AI agent frameworks — including MCP server registration.'
            }, {
              icon: <Diagram3 size={22} />,
              title: 'Map',
              text: 'Arkodomo builds an identity relationship graph — every machine identity, its owner, its access scope, and its live usage telemetry.'
            }, {
              icon: <Eye size={22} />,
              title: 'Detect',
              text: 'The behavioral engine flags unused identities, overprivileged accounts, lateral movement signals, and policy violations in real time.'
            }, {
              icon: <ArrowRepeat size={22} />,
              title: 'Enforce',
              text: 'Automated credential rotation, expiration enforcement, lifecycle cleanup, and governance exports — policy as code, not policy as hope.'
            }].map((s, i) => (
              <Col key={i} md={6} lg={3}>
                <Card bg="dark" text="light" className="h-100">
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <span style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(246,196,83,0.75)', textTransform: 'uppercase' }}>Step {String(i + 1).padStart(2, '0')}</span>
                    </div>
                    <div className="card-icon-badge">{s.icon}</div>
                    <Card.Title className="h5">{s.title}</Card.Title>
                    <Card.Text className="text-white-50 mb-0">{s.text}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Why It Matters */}
      <section className="py-5 bg-dark bg-opacity-50">
        <Container>
          <h2 className="h3 text-light mb-4 section-heading">Why It Matters</h2>
          <Row className="g-4">
            {[{
              title: 'AI agents need identity infrastructure built for them',
              text: 'AI agents are autonomous, cross-boundary, and multiplying fast. They require dynamic, time-bound credentials, delegation chain governance, and MCP-native authentication — not the static IAM patterns designed for human logins.'
            }, {
              title: 'Machine identities are your largest attack surface',
              text: 'Compromised service accounts and workload credentials are behind the majority of enterprise cloud breaches. Behavioral visibility and anomaly detection catch what perimeter tools miss entirely.'
            }, {
              title: 'Credential sprawl is a governance crisis',
              text: 'Enterprises carry thousands of unmanaged service accounts — no owners, no expiration, no policy. Arkodomo maps, scores, and enforces lifecycle at scale so security teams aren\'t flying blind.'
            }].map((cap, i) => (
              <Col key={i} md={4}>
                <Card bg="dark" text="light" className="h-100">
                  <Card.Body className="p-4">
                    <Card.Title className="h6 mb-3">{cap.title}</Card.Title>
                    <Card.Text className="text-white-50 mb-0">{cap.text}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Security & Trust */}
      <section id="security" className="py-5">
        <Container>
          <h2 className="h3 text-light mb-4 section-heading">Security & Trust</h2>
          <ul className="check-icon-list">
            {[
              'Customer-hosted deployment — your infrastructure, your perimeter. No customer data leaves your environment.',
              'Encrypted, authenticated connections. Least-privilege enforced at the policy layer.',
              'Ephemeral credentials by design. No persistent secrets stored by Arkodomo.',
              'Zero overhead on live traffic — no latency impact, no additional failure modes for production systems.',
              'Additive, not disruptive. Works alongside your existing IAM, PAM, and secrets infrastructure.'
            ].map((item, i) => (
              <li key={i}><CheckCircleFill size={15} /><span>{item}</span></li>
            ))}
          </ul>
        </Container>
      </section>

      {/* Roadmap */}
      <section className="py-5 bg-dark bg-opacity-50">
        <Container>
          <h2 className="h3 text-light mb-4 section-heading">Roadmap</h2>
          <div className="roadmap-timeline">
            <div className="roadmap-item">
              <span className="roadmap-badge now d-block mb-1">Now</span>
              <p className="fw-semibold text-light mb-1">Federated Identity Broker</p>
              <p className="text-white-50 mb-0">Credential issuance via a unified policy model across cloud IAM, orchestration platforms, and secrets infrastructure. The secure foundation everything else builds on.</p>
            </div>
            <div className="roadmap-item">
              <span className="roadmap-badge next d-block mb-1">Next</span>
              <p className="fw-semibold text-light mb-1">Identity Graph + Telemetry</p>
              <p className="text-white-50 mb-0">Usage log ingestion, identity relationship mapping, role assumption tracking, and AI-based risk scoring to surface overprivileged and dormant identities.</p>
            </div>
            <div className="roadmap-item">
              <span className="roadmap-badge later d-block mb-1">Later</span>
              <p className="fw-semibold text-light mb-1">Behavioral Detection + Governance</p>
              <p className="text-white-50 mb-0">ML-driven anomaly detection engine, automated lifecycle enforcement, compliance audit exports, and policy drift alerting across environments.</p>
            </div>
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-5">
        <Container>
          <h2 className="h3 text-light mb-4 section-heading">FAQ</h2>
          <Accordion className="accordion-dark" data-bs-theme="dark">
            <Accordion.Item eventKey="0">
              <Accordion.Header>How is this different from CyberArk or HashiCorp Vault?</Accordion.Header>
              <Accordion.Body>PAM tools manage vaults and privileged access for humans. Vault manages secrets. Arkodomo is purpose-built for machine identity lifecycle at scale — provisioning, relationship mapping, behavioral anomaly detection, and automated governance. We sit above existing PAM and secrets infrastructure, not beside it.</Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>Do you replace our existing IAM or SSO?</Accordion.Header>
              <Accordion.Body>No. Arkodomo is an identity broker that layers above your existing identity systems. Your IAM, SSO, and secrets infrastructure stay in place — we add visibility, policy enforcement, anomaly detection, and lifecycle governance on top.</Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="2">
              <Accordion.Header>Are you inline in request flows?</Accordion.Header>
              <Accordion.Body>No. Arkodomo manages identity lifecycle and credential issuance — we are not in the critical path of live requests. That means zero latency impact and no additional failure modes introduced into production traffic.</Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="3">
              <Accordion.Header>How do you deploy?</Accordion.Header>
              <Accordion.Body>A lightweight agent deploys within your infrastructure perimeter — no inbound access required, no customer data leaves your environment, all connections encrypted and authenticated. Native connectors reach cloud IAM, orchestration platforms, secrets managers, CI/CD systems, and more.</Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="4">
              <Accordion.Header>How do you handle long-lived secrets?</Accordion.Header>
              <Accordion.Body>We avoid storing them. Arkodomo prefers credential federation and time-bound issuance. Where long-lived secrets exist in your environment, we track usage, enforce rotation policies, and surface anomalous access — without holding the secret itself.</Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="5">
              <Accordion.Header>Can we adopt incrementally?</Accordion.Header>
              <Accordion.Body>Yes, by design. Start with a single environment and expand at your own pace. Our integration model is additive — connectors attach to existing infrastructure, not replace it.</Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="6">
              <Accordion.Header>How does Arkodomo handle AI agent identities?</Accordion.Header>
              <Accordion.Body>AI agents are first-class identities in Arkodomo. Each agent receives a cryptographic identity (SPIFFE SVID), capability-scoped credentials with configurable TTLs, and a delegation chain that governs sub-agent authority. Arkodomo also provides native MCP (Model Context Protocol) authentication — translating agent identities into OAuth 2.1 tokens scoped to specific tools — so agents can access enterprise systems without static API keys or human credentials.</Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="py-5">
        <Container>
          <div className="cta-gradient">
            <Row className="align-items-center g-3">
              <Col md={8}>
                <h3 className="h3 text-light mb-2">Your agents and machines need identities built for them.</h3>
                <p className="mb-0 text-white-50">Start with your highest-risk environments — AI agents, CI/CD workloads, or multi-cloud service accounts.</p>
              </Col>
              <Col md={4} className="d-flex flex-wrap gap-3 justify-content-md-end">
                <Button variant="primary" onClick={() => onOpenWaitlist && onOpenWaitlist('request-access')}>Join the Waitlist</Button>
                <Button variant="outline-light" onClick={() => onOpenWaitlist && onOpenWaitlist('design-partner')}>Design Partner</Button>
              </Col>
            </Row>
          </div>
        </Container>
      </section>
    </>
  );
}

export default Home;
